import { i18n } from "./i18nService";
import { getSdk } from "./sdkService";
import { trackEvent, sendUserEvent } from "./analyticsService";
import { redirectTo, getNavigationReferences } from "./navigationService";
import { getEpisodeLabel, getVoucherDurationText } from "./copyService";
import {
  getGlobalButtonFocusPath,
  getConfigForModalWithCloseButton,
  getGlobalButtonInfo
} from "../errors/globalButtons";
import { showAndReportUserRelatedError } from "../errors/utils";
import { getAppState } from "../state";
import { historyMethods } from "../utils/historyMethods";
import { isForceLogout } from "../utils/isForceLogout";
import { routes } from "../routes";
import { showInvalidStatusModal } from "./modalsService";
import { showForceLogoutError } from "./modalsService";
import * as Sentry from "@sentry/browser";

const PAGE = "detail";
const SIGNUP = "signup";
const LOGIN = "login";
const LOGIN_SIGNUP = "login_signup";
const PLAYBACK_TIME = "playbackTime";
const MEDIA_LISTS = "media_lists";
const MY_STARZ_LIST = "mystarzlist";
const HISTORY_LIST = "historylist";
const WATCHLIST = "watchlist";
const PROGRESS = "progress";
const addToWatchlistFocusPath = "buttonWatchlist";
let platformOrigin;

/**
 * Gets platform origin.
 * @returns {String} Platform origin label based on its name and year.
 * @private
 */
async function getPlatformOrigin() {
  if (!platformOrigin) {
    const sdk = await getSdk();
    const platform = await sdk.systemManager.device.getPlatform();
    const year = await sdk.systemManager.device.getYear();
    platformOrigin = platform + year;
  }
  return platformOrigin;
}

/**
 * Gets an object containing an empty media list array.
 * @returns {Object} Object containing empty media lists array.
 * @private
 */
function getEmptyMediaList() {
  return {
    media_lists: []
  };
}

/**
 * Adds a title ID to user's "My List" along with sending tracking events or handles medialist request
 * error in case it fails. Needs a React component class context since this method is making use of
 * this.state and other inner context properties (this.closeModal()).
 * @private
 */
async function addToMediaList() {
  const sdk = await getSdk();
  const {
    match: {
      params: { titleId }
    },
    history,
    stateActions: { showGlobalModal, setForceLogout },
    setFocus
  } = this.props;
  try {
    const origin = await getPlatformOrigin();
    await sdk.mediaManager.addTitleBodytoMediaList({
      origin,
      media_id: titleId,
      list_name: MY_STARZ_LIST
    });
    this.setState({ isSavedInMyWatchlist: true });
    trackEvent(PAGE, "add to watchlist", titleId);
    sendUserEvent({
      module_id: history.location.state ? history.location.state.moduleId : null,
      id: titleId,
      action: "wish_list",
      value: "add"
    });
  } catch (e) {
    await setForceLogout(e);
    Sentry.captureException(e);
    // if Force logout show the popup
    if (!!getAppState().forceLogout) {
      await showForceLogoutError.call(this);
      return;
    } else {
      if (!this.isExiting) {
        showGlobalModal(
          getConfigForModalWithCloseButton(
            {
              text: i18n.get("pages.detail.errors.couldNotAddToMyWatchlist")
            },
            this.closeModal.bind(this, addToWatchlistFocusPath)
          )
        );
        setFocus(getGlobalButtonFocusPath("close"));
      }
    }
  }
}

/**
 * Removes a title ID from user's "My List" along with sending tracking events or handles medialist request
 * error in case it fails. Needs a React component class context since this method is making use of
 * this.state and other inner context properties (this.closeModal()).
 * @private
 */
async function removeFromMediaList() {
  const sdk = await getSdk();
  const {
    match: {
      params: { titleId }
    },
    history,
    stateActions: { showGlobalModal, setCarouselItemFocus, setForceLogout },
    setFocus
  } = this.props;
  try {
    // If title is not included in mystarzlist when accessing its detail page for the first
    // time, medialists retrieved data won't include any mystarzlist information, including
    // ID. So for title's removal from this list, if user adds the title and wants to remove it
    // afterwards, mystarzlist ID will have to be requested.
    if (!this.mystarzlistId) {
      this.mystarzlistId = await getMyListId(this.state.id);
    }
    await sdk.mediaManager.removeTitleFromMediaList(this.mystarzlistId, titleId);
    this.setState({ isSavedInMyWatchlist: false });
    trackEvent(PAGE, "remove from watchlist", titleId);
    sendUserEvent({
      module_id: history.location.state ? history.location.state.moduleId : null,
      id: titleId,
      action: "wish_list",
      value: "remove"
    });
    history.location.referrer === routes.mylibrary && setCarouselItemFocus(0);
  } catch (e) {
    await setForceLogout(e);
    Sentry.captureException(e);
    // if Force logout show the popup
    if (!!getAppState().forceLogout) {
      await showForceLogoutError.call(this);
      return;
    } else {
      if (!this.isExiting) {
        showGlobalModal(
          getConfigForModalWithCloseButton(
            { text: i18n.get("pages.detail.errors.couldNotRemoveFromMyWatchlist") },
            this.closeModal.bind(this, addToWatchlistFocusPath)
          )
        );
        setFocus(getGlobalButtonFocusPath("close"));
      }
    }
  }
}

/**
 * Checks if there's "historylist" (means movie/episodes have been watched) to save its titles. Needs
 * a React component class context since this method is making use of inner context properties (this.historyList).
 * @param {Array} historyList - Array containing "historylist" object.
 * @private
 */
function checkHistoryListData(historyList) {
  historyList.length && (this.historyList = historyList[0].titles);
}

/**
 * Checks if there's "mystarzlist" (means movie/series has been saved there) to set
 * an state showed by "My Watchlist" button. Needs a React component class context since
 * this method is making use of this.state and other inner context properties (this.mystarzlistId).
 * @param {Array} myList - Array containing "mystarzlist" object.
 * @private
 */
function checkMyListData(myList) {
  myList.length && (this.mystarzlistId = myList[0].id);
  this.setState({ isSavedInMyWatchlist: !!myList.length });
}

/**
 * Checks if there's "watchlist" (means movie/series has been started watching) and saves last episode
 * data if title is a series. Progress will be saved whether title is a movie or a series. Needs a React
 * component class context since this method is making use of inner context properties.
 * @private
 */
function checkWatchlistData() {
  if (this.watchlist.length) {
    if (this.isSeries) {
      this.lastSeenEpisode = this.watchlist[0].titles[0];
      hasSeasonAndEpisodeNumber(this.lastSeenEpisode) &&
        (this.resumeEpisode = getEpisodeLabel(
          this.lastSeenEpisode.tvSeasonNumber,
          this.lastSeenEpisode.tvSeasonEpisodeNumber
        ));
    }
    this.progress = this.watchlist[0].titles[0].progress;
  }
}

/**
 * Checks if last seen episode has tvSeasonNumber and tvSeasonEpisodeNumber properties.
 * @param {Object} episode - Title object of the last seen episode (from watchlist data).
 * @returns {Boolean} If episode has season and episode number properties or not.
 * @private
 */
function hasSeasonAndEpisodeNumber(episode) {
  return !!(episode.tvSeasonNumber && episode.tvSeasonEpisodeNumber);
}

/**
 * Retrieves if user has a title saved in 'mystarzlist' to return this list's id, in order
 * to be used for title removal from this list.
 * @param {String} titleId - Title ID.
 * @returns {String} - My List ID.
 * @private
 */
async function getMyListId(titleId) {
  try {
    const sdk = await getSdk();
    const mystarzlist = await sdk.mediaManager.getUserMediaLists({
      titleId,
      listName: MY_STARZ_LIST
    });
    return mystarzlist[MEDIA_LISTS].length && mystarzlist[MEDIA_LISTS][0].id;
  } catch (e) {
    Sentry.captureException(e);
    throw e;
  }
}

/**
 * Requests user media lists.
 * @param {String} titleId - Title ID.
 * @param {Function} callback - Callback to execute.
 * @returns {Function} Callback to be executed based on medialist results.
 * @public
 */
export async function getUserMediaLists(titleId, callback) {
  let mediaLists;
  const appState = getAppState();
  if (!appState.userMediaCached) {
    // avoidNetworkCalls flag is added by the player when going back ONLY if the network was lost and will be used here
    // not to make the detail page dependant on having network connectivity to request user's media list
    if (!appState.avoidNetworkCalls) {
      const sdk = await getSdk();
      const origin = await getPlatformOrigin();
      try {
        appState.userMediaCached = await sdk.mediaManager.getUserMediaLists({
          // FIXME: refactor on url building process on SDK side to avoid making devices request here in the app. Same applies to mediaAssetTypes.
          origin,
          titleId,
          fields:
            "id,seriesId,title,tvSeasonNumber,tvSeasonEpisodeNumber,description,shortDescription,guid,media.expirationDate,media.availableDate,media.content.releases.pid,media.content.releases.url,media.content.assetTypes,media.content.streamingUrl,media.content.duration,peg$FourkProgramId,media.guid,peg$AddonContent",
          scope: "deep",
          // FIXME: mocked pageSize value, which limits the results returned in the mediaList request (e.g. if you've seen 25 episodes but
          // pageSize: 20, it will show only the last 20 results, resulting from historyList+watchlist+mystarzlist results). Maybe this value
          // should be handled according to episodesList length. In any case, this will be dealt with when Series play button detail page is done.
          // scope: "deep" requires pageSize param and seems to be needed to pass episode metadata from detailPage right away to the player.
          // Currently set as 999 as it is the maximum results allowed by SPX.
          pageSize: 999
        });
      } catch (e) {
        const forceLogoutStatus = await isForceLogout(e);
        if (forceLogoutStatus === true) {
          appState.forceLogout = true;
        }
        Sentry.captureException(e);
        appState.userMediaCached = getEmptyMediaList();
      }
    } else {
      appState.userMediaCached = getEmptyMediaList();
    }
  }
  delete appState.avoidNetworkCalls;
  mediaLists = appState.userMediaCached;
  return mediaLists[MEDIA_LISTS].length && callback(mediaLists[MEDIA_LISTS]);
}

/**
 * Handles My List button callback, either for adding or for removing a given title to/from this list. In
 * case user is not logged or user's account status is Prospect, a modal will show up to let the user know
 * about the next steps to take in order to use this feature. Needs a React component class context since
 * this method is making use of this.state and other inner context properties (this.closeModal()).
 * @returns {Function} - A function to add/remove a title in case user is logged and not prospect.
 * @public
 */
export async function myListHandler() {
  const {
    applicationState: { userLogged },
    setFocus
  } = this.props;
  if (userLogged) {
    return this.state.isSavedInMyWatchlist ? await removeFromMediaList.call(this) : await addToMediaList.call(this);
  } else {
    const { nextPageReference, backPageReference } = getNavigationReferences.call(this);
    const navigationReferences = {
      ...(nextPageReference && { nextPage: nextPageReference }),
      ...(backPageReference && { backDestination: backPageReference })
    };
    const modalText = !!getAppState().voucherData
      ? `${i18n.get("pages.detail.invalidAction.joinToPlay.paragraph1")}${i18n.get(
          "pages.detail.invalidAction.joinToPlay.paragraph2"
        )}`
      : i18n.get("pages.detail.invalidAction.loginToAddToWatchlist");
    const buttons = [
      {
        ...getGlobalButtonInfo(LOGIN),
        buttonHandler: redirectTo.bind(this, routes.pin, navigationReferences, historyMethods.REPLACE)
      },
      ...(!!getAppState().voucherData
        ? [
            {
              ...getGlobalButtonInfo(SIGNUP, getVoucherDurationText(getAppState().voucherData.duration.duration)),
              buttonHandler: () => {
                trackEvent(SIGNUP, "details_popup_user_selects_joinnow");
                redirectTo.call(this, routes.signup);
              }
            }
          ]
        : []),
      {
        ...getGlobalButtonInfo("cancel"),
        buttonHandler: this.closeModal.bind(this, addToWatchlistFocusPath)
      }
    ];
    showAndReportUserRelatedError.call(this, { modalText, buttons });
    setFocus(getGlobalButtonFocusPath(LOGIN));
  }
  return false;
}

/**
 * Checks if there's progress data saved on the title and returns its playback time if found.
 * Needs a React component class context since this method might make use of this.progres.
 * @returns {Number} Number representing content resuming playback time in case it has been
 * started watching or 0 if not.
 * @public
 */
export function getProgressData() {
  return (this.progress && this.progress[PLAYBACK_TIME]) || 0;
}

/**
 * Checks data present in mediaLists array. Needs a React component class context since
 * this method is making use of inner context variables (this.watchlist...).
 * This method is used for detail page purposes.
 * @param {Array} mediaLists - Array of media lists objects.
 * @public
 */
export function checkMediaListsData(mediaLists) {
  const historyList = mediaLists.filter(list => list.name === HISTORY_LIST);
  const mystarzlist = mediaLists.filter(list => list.name === MY_STARZ_LIST);
  this.watchlist = mediaLists.filter(list => list.name === WATCHLIST);

  checkHistoryListData.call(this, historyList);
  checkMyListData.call(this, mystarzlist);
  checkWatchlistData.call(this);
}

/**
 * Checks if there's "historylist" (means movie/episodes have been watched) to save
 * its progress in titles objects.
 * @param {Array} listEpisodes - Array containing the episodes list.
 * @param {Array} historyListTitles - Array containing "historylist" object.
 * @param {Array} watchListTitles - Array containing "watchlist" object.
 * @returns {Array} Episodes array.
 * @public
 */
export function checkHistoryProgress(listEpisodes, historyListTitles, watchListTitles) {
  const episodes = listEpisodes.reduce((accumulator, current) => {
    let historyMatch =
      (historyListTitles && historyListTitles.length && historyListTitles.find(el => current.id === el.id)) ||
      (watchListTitles && watchListTitles.find(el => current.id === el.id));
    historyMatch && (current[PROGRESS] = historyMatch.progress);
    accumulator.push(current);
    return accumulator;
  }, []);

  episodes.length &&
    episodes.map(
      episode =>
        historyListTitles &&
        historyListTitles.length &&
        historyListTitles.find(historyItem => historyItem.id === episode.id) &&
        watchListTitles &&
        !watchListTitles.find(watchItem => watchItem.id === episode.id) &&
        episode.progress &&
        episode.progress[PLAYBACK_TIME] &&
        (episode.progress[PLAYBACK_TIME] = 0)
    );

  return episodes;
}
